/**
 * TRUS-702 (Integrated Inbox, phase 1): helpers shared by the staff thread,
 * the member portal thread and their badges.
 *
 * The member DM is the one thread per member. Each message says how it
 * travelled (`channel`) and, for an outbound SMS / email, how delivery went
 * (`delivery_status`). Older servers send neither; every helper here treats a
 * missing field as "nothing to show".
 */
import type { MessageChannel, ReplyBlockReason, ReplyChannel, ReplyOptions } from '@/services/messagingApi';

type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

/**
 * Normalise a server channel value. The member portal's inbox has long sent
 * capitalised names ("SMS", "Phone", "Workspace"); the TRUS-702 fields are
 * lowercase. Unknown values map to null (no badge).
 */
export function normalizeChannel(raw: unknown): MessageChannel | null {
  if (typeof raw !== 'string' || !raw) return null;
  const v = raw.trim().toLowerCase();
  switch (v) {
    case 'portal':
    case 'sms':
    case 'email':
    case 'call':
    case 'voicemail':
    case 'note':
    case 'chat':
    case 'system':
    case 'campaign':
      return v;
    case 'phone':
    case 'voice':
      return 'call';
    case 'whatsapp':
      return 'sms';
    case 'workspace':
      return 'chat';
    default:
      return null;
  }
}

/** A staff-only internal note (TRUS-702), by channel or by metadata. */
export function isInternalNote(message: { channel?: unknown; metadata?: Record<string, any> | null } | null | undefined): boolean {
  if (!message) return false;
  if (normalizeChannel(message.channel) === 'note') return true;
  return message.metadata?.internal_note === true;
}

/** Label for a channel badge. chat / system get no badge. */
export function channelLabel(channel: MessageChannel, t: TFn): string | null {
  switch (channel) {
    case 'portal':
      return t('messaging.replyChannel.portal', 'Portal');
    case 'sms':
      return t('messaging.replyChannel.sms', 'SMS');
    case 'email':
      return t('messaging.replyChannel.email', 'Email');
    case 'call':
      return t('messaging.replyChannel.call', 'Call');
    case 'voicemail':
      return t('messaging.replyChannel.voicemail', 'Voicemail');
    case 'note':
      return t('messaging.replyChannel.note', 'Note');
    case 'campaign':
      return t('messaging.replyChannel.campaign', 'Campaign');
    default:
      return null;
  }
}

/** Plain-language reason a reply channel is closed or a send was blocked. */
export function blockReasonText(reason: ReplyBlockReason | string | null | undefined, t: TFn): string {
  switch (reason) {
    case 'opted_out':
      return t('messaging.replyChannel.reason.optedOut', 'The member opted out of this channel');
    case 'no_phone':
      return t('messaging.replyChannel.reason.noPhone', 'No mobile number on file');
    case 'no_email':
      return t('messaging.replyChannel.reason.noEmail', 'No email address on file');
    case 'no_portal_login':
      return t('messaging.replyChannel.reason.noPortalLogin', 'The member has no portal login');
    case 'sender_not_configured':
      return t('messaging.replyChannel.reason.senderNotConfigured', 'Your organization has no sender set up for this channel');
    case 'consent_missing':
      return t('messaging.replyChannel.reason.consentMissing', 'No consent on file for this channel');
    default:
      return t('messaging.replyChannel.reason.unavailable', 'Not available for this member');
  }
}

/**
 * Whether staff can pick a reply channel. Portal is never refused by the
 * server — `no_portal_login` only warns that the member cannot read it yet —
 * so it stays selectable; the others follow `allowed`.
 */
export function isSelectableReplyOption(option: { channel: ReplyChannel; allowed: boolean } | null | undefined): boolean {
  return !!option && (option.allowed || option.channel === 'portal');
}

/**
 * The channel a reply actually goes on: the staff pick if it is still
 * selectable, else the server's default (which is `portal` when nothing is
 * allowed — never silently a note). Null when there are no reply options
 * (older backend, not a member DM): the composer then sends as it always has.
 */
export function effectiveReplyChannel(options: ReplyOptions | null | undefined, picked: ReplyChannel | null): ReplyChannel | null {
  if (!options || !options.options?.length) return null;
  const selectable = (c: ReplyChannel | null | undefined) =>
    !!c && isSelectableReplyOption(options.options.find((o) => o.channel === c));
  if (selectable(picked)) return picked;
  if (options.default) return options.default;
  return options.options.find((o) => o.allowed && o.channel !== 'note')?.channel ?? null;
}

export interface DeliveryBlocked {
  reason: string | null;
  detail: string | null;
}

/**
 * A 422 `{code: "delivery_blocked", reason, detail}` from POST /messages,
 * whether FastAPI wrapped it in `detail` or not. Null for any other error.
 */
export function deliveryBlockedOf(error: unknown): DeliveryBlocked | null {
  const err = error as { status?: number; data?: any; response?: { status?: number; data?: any } } | null;
  if (!err) return null;
  const data = err.data ?? err.response?.data;
  if (!data || typeof data !== 'object') return null;
  const body = data.code === 'delivery_blocked' ? data : data.detail && typeof data.detail === 'object' ? data.detail : null;
  if (!body || body.code !== 'delivery_blocked') return null;
  return {
    reason: typeof body.reason === 'string' ? body.reason : null,
    detail: typeof body.detail === 'string' ? body.detail : null,
  };
}
